/*
  Predictive search, on Dawn's element: the field, the results Shopify renders from sections/predictive-search.liquid
  and the combobox keyboard pattern
  (https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-autocomplete-list/). What it adds to Dawn's: one
  request alive at a time (an older answer never overwrites a newer one), a message when a request fails, results per
  type from the theme setting, ids that belong to this field alone (the header and the search page both hold one), a
  layer that dims the page behind the header's bar and closes the results on a tap, results that stay in the search
  drawer while it is open, no regular expression built from the shopper's text, and the standard event's promise
  observed.
*/

// Answers by request, shared by every field on the page.
const predictiveAnswers = sectionAnswers({ size: 20 });

// The markup of the results, from the answer to the section request.
const resultsOf = (text) => {
  const section = new DOMParser()
    .parseFromString(text, 'text/html')
    .getElementById('shopify-section-predictive-search');
  if (!section) throw new Error('Predictive search answered without its section');
  return section.innerHTML;
};

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.results = this.querySelector('[data-predictive-results]');
    this.status = this.querySelector('[data-predictive-status]');
    this.scrim = this.querySelector('[data-predictive-scrim]');
    this.panel = this.querySelector('[data-predictive-panel]');
    this.searchTerm = '';
    this.isOpen = false;
    this.shown = false;
    this.request = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.lifetime = new AbortController();
    const { signal } = this.lifetime;
    // The search drawer around the field (snippets/header-search.liquid), where the results are the drawer's content.
    this.drawer = this.closest('dialog');

    this.input.form.addEventListener('submit', (event) => this.onFormSubmit(event), { signal });
    this.input.addEventListener('focus', () => this.onFocus(), { signal });
    this.input.addEventListener(
      'input',
      debounce((event) => this.onChange(event), 300),
      { signal }
    );
    this.addEventListener('focusout', (event) => this.onFocusOut(event), { signal });
    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    // A tap on the dimmed page closes the results: on iOS a tap on something that is not a control does not move the
    // focus, so the focus leaving the field cannot be the only way out. The listener is on the layer itself (iOS
    // delivers the click of an element that has its own listener), and the layer stays until the click, so the tap
    // cannot fall through to a link behind it.
    this.scrim?.addEventListener('click', () => this.close(), { signal });
    // The search drawer's results close with the drawer, and come back as it opens again and its field takes the focus.
    this.drawer?.addEventListener('close', () => this.close(), { signal });
    // Keeping the focus in the field while the pointer is pressed on the results is what lets a click on a result land
    // (Safari gives buttons no focus, so the field would blur first and the panel close under the pointer).
    this.panel.addEventListener('mousedown', (event) => event.preventDefault(), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.lifetime?.abort();
    this.roomTracker?.abort();
    this.roomFrame?.cancel();
    this.request?.abort();
    this.viewObserver?.disconnect();
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange(event) {
    // The debounced input of a field that lost focus in the meantime (a result was followed, the page is leaving)
    // asks for nothing.
    if (event?.type === 'input' && document.activeElement !== this.input) return;
    const term = this.getQuery();
    this.searchTerm = term;

    if (!term) {
      this.close();
      this.results.replaceChildren();
      this.removeAttribute('results');
      return;
    }

    this.getSearchResults(term);
  }

  onFormSubmit(event) {
    if (!this.getQuery()) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    this.searchTerm = '';
    this.request?.abort();
    this.close();
    this.results.replaceChildren();
    this.removeAttribute('results');
  }

  onFocus() {
    const term = this.getQuery();
    if (!term) return;

    if (this.searchTerm !== term) {
      // The field holds text it has not searched for yet (on the search page every field opens with its terms).
      this.onChange();
    } else if (this.hasAttribute('results')) {
      this.open();
    } else {
      this.getSearchResults(term);
    }
  }

  // In the search drawer the results stay while the focus moves on inside it (its close button, a tap on its title).
  onFocusOut(event) {
    if (!this.drawer && !this.contains(event.relatedTarget)) this.close();
  }

  onKeydown(event) {
    // Escape closes the results from anywhere in the field, its buttons too, and while the first answer is still on its
    // way (the panel is on screen, with its layer over the page, before it is open). In the search drawer the key
    // closes the drawer: a search field would only clear its text.
    if (event.key === 'Escape') {
      if (this.drawer) {
        event.preventDefault();
        this.drawer.close();
      } else if (this.shown) {
        event.preventDefault();
        this.close();
      }
      return;
    }
    if (event.target !== this.input) return;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        // The arrows move through the results, not the text cursor; Down brings back results that were closed.
        if (!this.isOpen && !(event.key === 'ArrowDown' && this.hasAttribute('results'))) return;
        event.preventDefault();
        if (!this.isOpen) this.open();
        this.moveHighlight(event.key === 'ArrowDown' ? 1 : -1);
        break;
      case 'Enter': {
        const option = this.getHighlighted();
        if (!this.isOpen || !option) return;
        event.preventDefault();
        option.click();
        break;
      }
    }
  }

  // The options on screen, in the order of the markup, which is the order they are drawn in: the arrows follow it.
  getOptions() {
    return [...this.results.querySelectorAll('[role="option"]')].filter((option) => option.offsetParent !== null);
  }

  getHighlighted() {
    return this.results.querySelector('[role="option"][aria-selected="true"]');
  }

  moveHighlight(step) {
    const options = this.getOptions();
    if (!options.length) return;

    const current = this.getHighlighted();
    if (!current && step < 0) return;

    const index = options.indexOf(current);
    const next = options[(index + step + options.length) % options.length];
    this.highlight(next);
  }

  highlight(option) {
    this.getHighlighted()?.setAttribute('aria-selected', 'false');
    if (!option) {
      this.input.removeAttribute('aria-activedescendant');
      return;
    }
    option.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  getSearchResults(term) {
    const limit = Number(this.dataset.perType) || 5;
    const url = new URL(routes.predictive_search_url, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', 'predictive-search');
    // Shopify's default types leave out articles.
    url.searchParams.set('resources[type]', 'query,product,collection,page,article');
    url.searchParams.set('resources[limit]', limit);
    url.searchParams.set('resources[limit_scope]', 'each');

    this.request?.abort();
    const request = new AbortController();
    this.request = request;

    this.setAttribute('loading', '');
    this.announceShown();
    this.setStatus(this.dataset.loadingText);
    const deferred = this.dispatchSearchUpdateEvent(term);

    const cached = predictiveAnswers.peek(url.href);
    if (cached !== null) {
      this.render(resultsOf(cached));
      deferred?.resolve({ totalCount: this.getTotalCount() });
      return;
    }

    // A newer request aborts this one, which then rejects as aborted and draws nothing.
    predictiveAnswers
      .get(url.href, request.signal)
      .then((text) => {
        this.render(resultsOf(text));
        deferred?.resolve({ totalCount: this.getTotalCount() });
      })
      .catch((error) => {
        deferred?.reject(error);
        if (error.name === 'AbortError' || this.request !== request) return;
        predictiveAnswers.drop(url.href);
        this.renderFailure();
      });
  }

  getTotalCount() {
    return Number(this.results.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    return standardEvent(window.StandardEvents?.SearchUpdateEvent, { search: { query } }, this);
  }

  render(markup) {
    // The option the input pointed at is about to leave the page.
    this.input.removeAttribute('aria-activedescendant');
    this.results.innerHTML = markup;
    this.scopeIds();
    this.removeAttribute('loading');
    this.setAttribute('results', '');
    this.setStatus(this.results.querySelector('[data-predictive-count]')?.textContent);
    this.open();
  }

  renderFailure() {
    const message = document.createElement('p');
    message.className = 'predictive-search__message';
    message.textContent = this.dataset.errorText;
    this.input.removeAttribute('aria-activedescendant');
    this.results.replaceChildren(message);
    this.removeAttribute('loading');
    // Not a result: coming back to the field, or Down, asks again instead of showing the failure.
    this.removeAttribute('results');
    this.setStatus(this.dataset.errorText);
    this.open();
  }

  // Shopify renders the same section for every field, so its ids are the same too: each field gives them its own
  // input's id as a prefix, and the field's aria-controls (`<input id>-results`) finds its list.
  scopeIds() {
    const prefix = this.input.id;
    this.results.querySelectorAll('[id]').forEach((element) => {
      element.id = `${prefix}-${element.id}`;
    });
    this.results.querySelectorAll('[aria-labelledby]').forEach((element) => {
      element.setAttribute('aria-labelledby', `${prefix}-${element.getAttribute('aria-labelledby')}`);
    });
  }

  setStatus(text) {
    if (this.status) this.status.textContent = text || '';
  }

  open() {
    this.setAttribute('open', '');
    this.isOpen = true;
    this.announceShown();
    // The list holds its answer now, so the room under its top is measured again: an empty list is not drawn (Dawn
    // hides an empty div), and its top read as 0 when the panel came up.
    this.roomFrame?.schedule();
    this.input.setAttribute('aria-expanded', this.getOptions().length ? 'true' : 'false');
    // A scrolling area is reachable from the keyboard (the options are not tab stops, the field drives them).
    reachScroller(this.results, { vertical: true, forced: true });
  }

  // The panel is on screen (waiting for an answer, or open), told once until it closes. The state is already set, so
  // whoever listens sees it. The markup hides the panel and the layer behind the bar, since the stylesheet that draws
  // them is linked without blocking; they show with the state.
  announceShown() {
    if (this.shown) return;
    this.shown = true;
    this.panel.hidden = false;
    if (this.scrim) {
      this.scrim.hidden = false;
      // The header listens: a header folded to its logo row unfolds while the bar's results are on screen, so the layer
      // that dims the page (position: fixed) has the window to itself and not a moved header.
      this.dispatchEvent(new CustomEvent('predictive-search:open', { bubbles: true }));
    }
    // A header that is not sticky scrolls away with the field: the results (and the layer over the page) go with it.
    // The drawer's field never does, and its first frames, as the drawer comes in, lie outside the window.
    if (!this.drawer) {
      this.viewObserver = new IntersectionObserver((entries) => {
        if (!entries[entries.length - 1].isIntersecting) this.close();
      });
      this.viewObserver.observe(this.input);
    }
    this.trackRoom();
  }

  // The on-screen keyboard shrinks the visual viewport, not the dvh unit: while the panel is up, the room under its
  // top (what a shopper can see) is a custom property the list's height reads, so the pinned row stays above the keys.
  trackRoom() {
    const viewport = window.visualViewport;
    if (!viewport || this.roomTracker) return;
    this.roomTracker = new AbortController();
    const { signal } = this.roomTracker;
    const update = () => {
      const top = this.results.getBoundingClientRect().top - viewport.offsetTop;
      this.style.setProperty('--search-room', `${Math.max(0, viewport.height - top)}px`);
    };
    update();
    // Scrolling fires faster than the screen draws: the room is measured once a frame.
    this.roomFrame = oncePerFrame(update);
    const measure = () => this.roomFrame.schedule();
    viewport.addEventListener('resize', measure, { signal });
    viewport.addEventListener('scroll', measure, { signal });
    // A page scroll moves the list's top (a header that scrolls away) without moving the visual viewport.
    window.addEventListener('scroll', measure, { signal, passive: true });
  }

  releaseRoom() {
    this.roomTracker?.abort();
    this.roomTracker = null;
    this.roomFrame?.cancel();
    this.style.removeProperty('--search-room');
  }

  close() {
    // An answer still on its way must not open a panel the shopper has left.
    this.request?.abort();
    this.request = null;
    this.highlight(null);
    this.removeAttribute('open');
    this.removeAttribute('loading');
    this.input.setAttribute('aria-expanded', 'false');
    this.isOpen = false;
    this.shown = false;
    this.panel.hidden = true;
    if (this.scrim) this.scrim.hidden = true;
    this.viewObserver?.disconnect();
    this.viewObserver = null;
    this.releaseRoom();
    this.setStatus('');
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
